import { Chip, Typography } from "@mui/material"
import type { FC } from "react"

import DisplayIds from "@/components/common/DisplayIds/DisplayIds"
import { NoValuePlaceholder } from "@/components/common/NoValuePlaceholder"
import type { Product } from "@/graphql/codegen/graphql"
import { ProductVersionChip } from "@/screens/Products/components/ProductVersionChip"

interface ProductInventoryHeaderInfoProps {
  product: Product
}

export const ProductInventoryHeaderInfo: FC<ProductInventoryHeaderInfoProps> = ({ product }) => {
  const currentVersion = product.versions?.find((version) => version.id === product.versionId)
  const productManager = product.classification?.productManager

  return (
    <div className='flex flex-[5_1_0] flex-col justify-between gap-2'>
      <Typography variant='overline' className='leading-3 tracking-[1px] text-gray-700'>
        AKROCHEM DESCRIPTION
      </Typography>

      <div className='flex items-center gap-3'>
        <Typography variant='h5' component='h2' className='text-gray-800'>
          {product.companyProductDescription}
        </Typography>
        {product.versionStatus && (
          <Chip
            label={product.isActive ? "Active" : "Pending"}
            variant='outlined'
            color={product.isActive ? "success" : "warning"}
            size='small'
          />
        )}
        {currentVersion && <ProductVersionChip version={currentVersion} />}
        <DisplayIds entityName='Product ID' publicId={product.publicId} externalSystemId={product.externalSystemId} />
      </div>

      <div className='flex items-center gap-6'>
        <Typography variant='body2' className='leading-6 tracking-[0.46px] text-gray-700'>
          Vendor Description: <span className='font-medium'>{product.vendorDescription || <NoValuePlaceholder />}</span>
        </Typography>
        <Typography variant='body2' className='leading-6 tracking-[0.46px] text-gray-700'>
          Vendor SKU: <span className='font-medium'>{product.vendorSku || <NoValuePlaceholder />}</span>
        </Typography>
        <Typography variant='body2' className='leading-6 tracking-[0.46px] text-gray-700'>
          Supplier: <span className='font-medium'>{product.company?.name || <NoValuePlaceholder />}</span>
        </Typography>
      </div>

      <div className='flex items-center gap-6'>
        <div className='flex items-center gap-2'>
          <Typography variant='body2' className='text-gray-700'>
            Category:
          </Typography>
          {product.classification?.name ? (
            <Chip label={product.classification.name} className='h-6 rounded-sm text-xs' />
          ) : (
            <NoValuePlaceholder />
          )}
        </div>
        <div className='flex items-center gap-2'>
          <Typography variant='body2' className='text-gray-700'>
            Class:
          </Typography>
          {product.classification?.name ? (
            <Chip label={product.classification.name} className='h-6 rounded-sm text-xs' />
          ) : (
            <NoValuePlaceholder />
          )}
        </div>
        <div className='flex items-center gap-2'>
          <Typography variant='body2' className='text-gray-700'>
            Product Manager:
          </Typography>
          {productManager ? (
            <Chip
              label={`${productManager.firstName} ${productManager.lastName}`}
              className='h-6 rounded-sm bg-tertiary-yellow-100 text-xs'
            />
          ) : (
            <NoValuePlaceholder />
          )}
        </div>
      </div>
    </div>
  )
}
